import React, { useState } from 'react';
import Popup from 'reactjs-popup';
import { useTranslation, Trans } from 'react-i18next';
import styles from './InAppCheckoutModal.module.scss';
import { useQuery } from '@apollo/client';

import { ReactComponent as CloseIcon } from '../../../../../assets/images/icons/close.svg';
import { PurchaseType, SupplementCartItem, SUPPLEMENT_BAG_IMAGES, getCartSKUs } from '../UpsellV2/UpsellV2';
import { ReactComponent as TrashIcon } from '../../../../../assets/images/icons/trash.svg';
import { getMultipleSupplementSubscriptionsPricingQuery } from '../../../../../graphql-operations/Subscription.graphql';
import * as types from '../../../../../types';
import { formattedCentsToDollars, getLoadingOrErrorElement } from '../../../../../lib/util';
import { getDomainConfig } from '../../../../../lib/domainConfig';

interface IInAppCheckoutModalProps {
  onPurchase: () => void;
  onOpen?: () => void;
  onClose: () => void;
  trigger: React.ReactElement;
  open: boolean;
  cartItems: SupplementCartItem[];
  isEligibleForFreeBag: boolean; // Dictates whether or not to show free bag offer
  onRemoveItem?: (index: number, close: () => void) => void;
  isUpsellLayout?: boolean;
}

export const getItemPrice = (purchaseType: PurchaseType): number => {
  return purchaseType === PurchaseType.Autoship ? 25 : 30;
};

export default function InAppCheckoutModal({
  onPurchase,
  onOpen,
  onClose,
  trigger,
  open,
  cartItems,
  onRemoveItem,
  isUpsellLayout,
  isEligibleForFreeBag,
}: IInAppCheckoutModalProps) {
  const { t } = useTranslation();
  const { baseDomain } = getDomainConfig();
  const [confirmButtonDisabled, setConfirmButtonDisabled] = useState(false);
  const { data, loading, error } = useQuery<
    types.gqlTypes.ECOMMERCE_getMultipleSupplementSubscriptionsPricing,
    types.gqlTypes.ECOMMERCE_getMultipleSupplementSubscriptionsPricingVariables
  >(getMultipleSupplementSubscriptionsPricingQuery, {
    variables: {
      input: { skusToAdd: getCartSKUs(cartItems) },
    },
    skip: cartItems.length === 0,
  });

  const cartContainsDiscountEligibleItems = cartItems.some((item) => item.eligibleForDiscount);
  const tax = formattedCentsToDollars(data?.getMultipleSupplementSubscriptionsPricing?.taxInCents || 0);
  const totalPrice = formattedCentsToDollars(data?.getMultipleSupplementSubscriptionsPricing?.totalInCents || 0);

  const loadingOrErrorElement = getLoadingOrErrorElement(loading, error);
  if (loadingOrErrorElement) {
    return (
      <Popup className="s3-popup" open={open} onClose={onClose} trigger={trigger} modal>
        {() => loadingOrErrorElement}
      </Popup>
    );
  }

  return (
    <Popup className="s3-popup" open={open} onOpen={onOpen} onClose={onClose} trigger={trigger} modal>
      {(close: () => void) => (
        <div className={styles.container}>
          {!isUpsellLayout && (
            <div className={styles.header}>
              <div className={styles.title}>{t('check_out')}</div>
              <div className={styles.closeIcon}>
                <CloseIcon onClick={() => close()} />
              </div>
            </div>
          )}
          <div className={styles.body}>
            <div className={styles.cartItemsContainer}>
              {cartItems.map((item, index) => (
                <div className={styles.cartItem} key={index}>
                  <div className={styles.cartItemDetails}>
                    {!isUpsellLayout && (
                      <div className={styles.cartItemImage}>
                        <img src={SUPPLEMENT_BAG_IMAGES[item.skuType].url} alt={item.skuType} />
                      </div>
                    )}
                    <div className={styles.cartItemText}>
                      <div className={styles.cartItemTitle}>{item.skuType}</div>
                      {!isUpsellLayout && item.purchaseType === PurchaseType.Autoship && (
                        <div className={styles.cartItemDescription}>
                          {t('auto_ships_every_weeks_weeks', { weeks: item.frequency })}
                        </div>
                      )}
                      {isUpsellLayout && item.purchaseType === PurchaseType.Autoship && (
                        <div className={styles.cartItemDescription}>
                          {t('ships_every_weeks_weeks_for_amount', {
                            weeks: item.frequency,
                            amount: formattedCentsToDollars(2500),
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                  {!isUpsellLayout && (
                    <div className={styles.cartItemPrice}>
                      <div className={styles.cartItemPriceValue}>
                        {t('price', { price: getItemPrice(item.purchaseType) })}
                      </div>
                      {onRemoveItem && (
                        <div className={styles.cartItemRemoveButton} onClick={() => onRemoveItem(index, close)}>
                          <TrashIcon />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {cartContainsDiscountEligibleItems && isEligibleForFreeBag && (
              <div className={styles.promotionContainer}>
                <div className={styles.promotionTitle}>{t('first_bag_free_special_offer')}</div>
                <div className={styles.promotionValue}>{'-$25'}</div>
              </div>
            )}
            <div className={styles.taxContainer}>
              <div className={styles.taxTitle}>{t('tax')}</div>
              <div className={styles.taxValue}>{tax}</div>
            </div>
            <div className={styles.totalDueContainer}>
              <div className={styles.totalDueTitle}>{t('total_due_today')}</div>
              <div className={styles.totalDueValue}>{totalPrice}</div>
            </div>
            <button
              className={styles.confirmOrderButton}
              onClick={() => {
                setConfirmButtonDisabled(true);
                onPurchase();
              }}
              disabled={confirmButtonDisabled}
            >
              {t('confirm_order')}
            </button>
            {!isUpsellLayout && (
              <>
                <button className={styles.continueShoppingButton} onClick={() => close()}>
                  {t('continue_shopping')}
                </button>
                <div className={styles.disclaimer}>
                  <Trans
                    i18nKey="by_tapping_confirm_order_you_accept_the_terms_conditions"
                    components={{
                      // eslint-disable-next-line jsx-a11y/anchor-has-content
                      tosLink: <a href={`https://${baseDomain}/tos`} target="_blank" rel="noopener noreferrer" />,
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </Popup>
  );
}
